import { z } from 'zod';
import { regionSlug } from '~/modules/business-manager/forms/domain/routing-eval';
import { getClosestBasicPlace } from '~/modules/location/util/get-closest-place';
import type { BasicPlace } from '~/modules/location/type';
import type { DropdownItem } from '../types';
import type { Branch } from '../util/extract-branch-from-network.server';
import type {
    PublicEnquiryForm,
    PublicEnquiryFormBranchOption,
    PublicEnquiryFormBranchPicker,
} from '../types/public-enquiry-form';

/** The picker's label when neither the form nor the page names it. */
export const DEFAULT_BRANCH_PICKER_LABEL = 'Send to branch';

/**
 * The GraphQL `EnquiryForm.branchPicker` object. Parsed rather than typed off
 * the generated schema so a response without it, or with a partial one,
 * degrades to "no branch question" instead of failing the whole form.
 */
const GRAPHQL_BRANCH_PICKER = z.object({
    enabled: z.boolean(),
    required: z.boolean().catch(true),
    label: z.string().nullish(),
    preselectByLocation: z.boolean().catch(false),
    options: z
        .array(
            z.object({
                id: z.coerce.number().int().positive(),
                title: z.string(),
                regionSegment: z.string().nullish(),
            }),
        )
        .catch([]),
});

/** Maps the form's GraphQL branch question onto the public view model. */
export function toPublicBranchPicker(
    raw: unknown,
): PublicEnquiryFormBranchPicker | null {
    const parsed = GRAPHQL_BRANCH_PICKER.safeParse(raw);
    if (!parsed.success) return null;
    const picker = parsed.data;
    return {
        enabled: picker.enabled,
        required: picker.required,
        label: picker.label?.trim() || '',
        preselectByLocation: picker.preselectByLocation,
        options: picker.options.map((option) => ({
            id: option.id,
            title: option.title,
            regionSegment: option.regionSegment?.trim() || null,
        })),
    };
}

/**
 * The branch question a form renders, from whichever source owns it:
 * - `form`: the configured form describes the question (Forms on, API serving it);
 * - `network`: the page's own branch list, the flat-settings behaviour.
 */
export interface ResolvedBranchPicker {
    source: 'form' | 'network';
    label: string;
    required: boolean;
    preselectByLocation: boolean;
    /** In the shape `useEnquiry` validates and saves branch ids against. */
    branchOptions: DropdownItem<Branch>[];
    /** Region slugs by branch id, for location preselection. */
    regionSegments: Record<number, string>;
}

const toBranchOption = (
    option: PublicEnquiryFormBranchOption,
): DropdownItem<Branch> => ({
    header: option.title,
    value: { branchId: option.id },
});

/**
 * Resolves the branch question. A form whose API response carries the
 * question owns it outright — `null` or switched off means no picker even
 * where the page has branches. A form that says nothing about it (`undefined`,
 * an API without the field) or no form falls back to the page's branch list
 * when the host asked for branches.
 */
export function resolveBranchPicker(
    form: PublicEnquiryForm | null,
    fallback: { includeBranch: boolean; branchOptions: DropdownItem<Branch>[] },
): ResolvedBranchPicker | null {
    const picker = form?.branchPicker;
    if (picker !== undefined) {
        if (!picker?.enabled || picker.options.length === 0) return null;
        return {
            source: 'form',
            label: picker.label || DEFAULT_BRANCH_PICKER_LABEL,
            required: picker.required,
            preselectByLocation: picker.preselectByLocation,
            branchOptions: picker.options.map(toBranchOption),
            regionSegments: Object.fromEntries(
                picker.options.flatMap((option) =>
                    option.regionSegment
                        ? [[option.id, option.regionSegment]]
                        : [],
                ),
            ),
        };
    }
    if (!fallback.includeBranch || fallback.branchOptions.length === 0) {
        return null;
    }
    return {
        source: 'network',
        label: DEFAULT_BRANCH_PICKER_LABEL,
        required: true,
        preselectByLocation: false,
        branchOptions: fallback.branchOptions,
        regionSegments: {},
    };
}

/**
 * The branch to preselect for where the visitor is. A form-driven picker
 * matches the location answer's region, then the IP region, against each
 * branch's region; the network list picks the branch nearest the chosen place.
 * Null when nothing matches or the picker does not preselect.
 */
export function preselectBranchId(
    picker: ResolvedBranchPicker,
    location: {
        place?: BasicPlace | null;
        regionName?: string | null;
        ipRegion?: string | null;
    },
): number | null {
    if (picker.source === 'network') {
        if (!location.place) return null;
        const closest = getClosestBasicPlace(
            location.place,
            picker.branchOptions.map((option) => option.value),
        ) as Branch | undefined;
        return closest?.branchId ?? null;
    }
    if (!picker.preselectByLocation) return null;
    for (const region of [location.regionName, location.ipRegion]) {
        const segment = regionSlug(region);
        if (!segment) continue;
        const match = Object.entries(picker.regionSegments).find(
            ([, branchSegment]) => branchSegment === segment,
        );
        if (match) return Number(match[0]);
    }
    return null;
}
